<template>
  <ContentfulHeroSections :item="item" :toc-info="tocInfo" />

  <ArticleTocLayout
    v-if="item.fields.contentSection && tocInfo?.showToc"
    id="toc-container"
    :mobile-title="$textDictionary['toc.mobileTitle'] ?? 'Table of content'"
    :aside-aria-label="
      $textDictionary['toc.asideAriaLabel'] ?? 'Table of content'
    "
    :nav-heading="$textDictionary['toc.navHeading'] ?? 'Content of page'"
    :open-toc-btn-action-label="
      $textDictionary['toc.openTocBtnActionLabel'] ?? 'open'
    "
    :prev-btn-label="$textDictionary['toc.prevBtnLabel'] ?? 'previous'"
    :next-btn-label="$textDictionary['toc.nextBtnLabel'] ?? 'next'"
    :toc-skip-btn-text="
      $textDictionary['toc.tocSkipBtnText'] ?? 'Skip Table of content'
    "
  >
    <LibGroupedSections :entries="item.fields.contentSection" />
  </ArticleTocLayout>
  <LibGroupedSections
    v-else-if="item.fields.contentSection"
    :entries="item.fields.contentSection"
  />

  <LibGroupedSections
    v-if="item.fields.callToAction"
    :entries="[item.fields.callToAction]"
    :as-section="true"
  />
</template>

<script setup lang="ts">
import type { IContentPage, LOCALE_CODE } from '~/lib/ContentfulService';
import { ArticleTocLayout } from '@hypercodestudio/basler-components';
import type { TOCInfo } from '~/types/TocType';
import { mapToFormattedDate } from '~/utils/mapper/mapToFormattedDate';
import { useHostname } from '~/composables/useHostname';

const { $textDictionary, $locale } = useNuxtApp();

const PAGETYPE_TO_ARTICLETYPE: Record<string, string> = {
  'Knowledge Article': 'TechArticle',
  Tutorial: 'TechArticle',
  Product: 'WebPage',
  News: 'NewsArticle',
  'Use Case': 'TechArticle',
  'Customer Story	': 'WebPage',
  Miscellaneous: 'WebPage'
};

const readingTime = ref(1);

interface Props {
  item: IContentPage;
}

const props = defineProps<Props>();

const tocInfo = computed<TOCInfo>(() => {
  return {
    showToc: props.item.fields.showToc,
    centeredText: !props.item.fields.showToc,
    date: props.item.fields?.date
      ? mapToFormattedDate(
          props.item.fields?.date.toString(),
          $locale.value as LOCALE_CODE
        )
      : undefined,
    readingTime: readingTime.value
  };
});

onMounted(async () => {
  if (tocInfo.value?.showToc) {
    const { data: rt } = await useFetch<number>(
      '/api/articles/getReadingTime',
      {
        query: {
          pageId: props.item.sys.id,
          lastUpdate: props.item.sys.updatedAt,
          locale: $locale.value
        }
      }
    );
    if (rt.value) {
      readingTime.value = rt.value;
    } else {
      if (document.getElementById('toc-container')) {
        const text = document.getElementById('toc-container')?.innerText;
        const images =
          document.getElementById('toc-container')?.getElementsByTagName('img')
            .length ?? 0;
        const videos =
          document
            .getElementById('toc-container')
            ?.getElementsByTagName('.media-block').length ?? 0;
        const words = text ? text.split(/\s+/).length : 0;

        const { data: rt } = await useFetch<number>(
          '/api/articles/setReadingTime',
          {
            query: {
              pageId: props.item.sys.id,
              lastUpdate: props.item.sys.updatedAt,
              images: images,
              videos: videos,
              words: words,
              locale: $locale.value
            }
          }
        );

        if (rt.value) {
          readingTime.value = rt.value;
        }
      }
    }
  }
});

const { $globalPageSettings } = useNuxtApp();
const locale = useLocale();
const serverUrlOrigin = useHostname();
const url = computed(() =>
  buildUrlString(locale.value, undefined, undefined, undefined, serverUrlOrigin)
);

const schema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': props.item.fields.format
    ? PAGETYPE_TO_ARTICLETYPE[props.item.fields.format]
    : 'WebPage',
  ...(props.item.fields.date && {
    datePublished: new Date(props.item.fields.date).toISOString(),
    dateModified: new Date(props.item.fields.date).toISOString()
  }),
  headline: props.item.fields.teaserTitle ?? undefined,
  description: props.item.fields.teaserText ?? undefined,
  image:
    props.item.fields.teaserImage?.fields?.image?.fields?.file?.url ??
    undefined,
  author: [
    {
      '@type': 'Organization',
      name: $globalPageSettings.value?.companyName ?? undefined,
      url: url.value,
      logo: $globalPageSettings.value?.companyLogo?.url ?? undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: $globalPageSettings.value?.companyStreet ?? undefined,
        postalCode: $globalPageSettings.value?.companyPostalCode ?? undefined,
        addressLocality: $globalPageSettings.value?.companyCity ?? undefined,
        addressRegion: $globalPageSettings.value?.companyRegion ?? undefined,
        addressCountry:
          locale.value?.split('-').at(-1)?.toUpperCase() ?? undefined
      }
    }
  ],
  isAccessibleForFree: true
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema.value),
      tagPriority: -1 // Optional: Sicherstellen, dass es zuletzt eingefügt wird
    }
  ]
});
</script>
